import { z } from 'zod';

import { httpClientInterface } from 'app-components/utils/core';
import { getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';

import {
  type DetailedQuote,
  DetailedQuoteSchema,
  GetDetailedQuotesErrorSchema,
  type GetDetailedQuotesRequest,
  QuotesMetaDataSchema,
} from './investmentsQuotesDetailedTypes';

export const GetDetailedQuotesResponseSchema = z
  .object({
    resources: z.array(z.unknown()),
    errors: z.array(GetDetailedQuotesErrorSchema).optional(),
    metaData: QuotesMetaDataSchema,
  })
  .transform(({ resources, errors, metaData }) => {
    const validQuotes: DetailedQuote[] = [];
    const invalidQuotes: Partial<DetailedQuote>[] = [];
    resources.forEach((rawQuote) => {
      const result = DetailedQuoteSchema.safeParse(rawQuote);
      if (result.success) {
        validQuotes.push(result.data);
      } else {
        invalidQuotes.push(rawQuote as Partial<DetailedQuote>);
      }
    });

    return { resources: validQuotes, errors, invalidQuotes, metaData };
  });

export const investmentsQuotesDetailedApi = {
  async getDetailedQuotes({
    symbols,
    params: { realTime = false, includeFallback = true } = {},
  }: GetDetailedQuotesRequest): Promise<DetailedQuote[]> {
    const url = new URL(`${getServiceUrl()}/v2/investments/quotes/detailed`);
    if (typeof realTime === 'boolean') {
      url.searchParams.append('realTime', realTime.toString());
    }
    if (typeof includeFallback === 'boolean') {
      url.searchParams.append('includeFallback', includeFallback.toString());
    }
    const requestUrl = url.toString();
    const body = { symbols };
    const response = await httpClientInterface.send('POST', requestUrl, body);
    const responseData = GetDetailedQuotesResponseSchema.parse(response.data);
    const { resources, invalidQuotes } = responseData;
    assert(!invalidQuotes?.length, 'Invalid Quotes Returned', {
      requestUrl,
      requestBody: body,
      responseBody: response.data,
      status: response.status,
      invalidQuotes,
    });
    // assert(!errors?.length, 'Server Rejected Quotes', { requestUrl, body, response, errors });
    return resources;
  },
};
